import React, {useCallback} from 'react'
import {defineMessage, useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {
  FacilityPills,
  FreeCancellationPill,
  MealPills,
  PricePill,
  PropertyTypePills,
  ReviewScorePill,
  StarRatingsPill,
  ThemePills
} from 'components/Filters/FilterOverlaySummary/FilterOverlaySummary'
import {getAllFilterKeys} from 'components/Filters/utils'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {
  getAnchorHotelId,
  getShowTotalPriceToggle
} from 'modules/sapiSearch/selectors'

import {Alert} from '@vio/atlas/Alert'
import {Button} from '@vio/atlas/Button'
import {HorizontalScroll} from '@vio/atlas/helpers/HorizontalScroll'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import minus from '@vio/atlas/utils/minus'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

const titleMessage = defineMessage({
  id: 'mapResults.filterMatches',
  defaultMessage:
    '{count, plural, =0 {We found no matches} one {Only 1 exact match} other {Only # exact matches}}'
})

const ScrollWrapper = styled.div`
  margin: ${cssTheme.layout.spacing.s400} ${minus(cssTheme.layout.spacing.s500)}
    0;
`

const HorizontalScrollComponent = styled(HorizontalScroll)`
  padding: 0 ${cssTheme.layout.spacing.s500};
  ${linariaMq.desktopXs} {
    row-gap: ${cssTheme.layout.spacing.s400};
  }
`

const ClearAllWrapper = styled.div`
  margin-left: ${minus(cssTheme.layout.spacing.s200)};
`

const VerticalDivider = styled.div`
  width: 1px;
  flex-shrink: 0;
  height: ${cssTheme.layout.spacing.s700};
  background-color: ${cssTheme.colors.background.warning.tertiary};
  margin: 0 ${cssTheme.layout.spacing.s200};
`

const AlertWrapper = styled(Alert)`
  padding: ${cssTheme.layout.spacing.s500};
`

const SubtitleWrapper = styled.span`
  margin-top: ${cssTheme.layout.spacing.s250};
  display: block;
`

const ContentWrapper = styled.div`
  margin: 0 0 ${cssTheme.layout.spacing.s500};
  ${linariaMq.desktopXs} {
    margin: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s400};
  }
  &.--margin-top {
    margin: ${cssTheme.layout.spacing.s200} 0 ${cssTheme.layout.spacing.s500};
  }
`

const allFilterKeys = getAllFilterKeys()

interface Props {
  numResults: number
}

const PillComponents = [
  FacilityPills,
  FreeCancellationPill,
  MealPills,
  PricePill,
  PropertyTypePills,
  ReviewScorePill,
  StarRatingsPill,
  ThemePills
]

export const PartialMatchBanner = ({numResults}: Props) => {
  const dispatch = useDispatch()
  const anchorHotelId = useSelector(getAnchorHotelId)
  const showTotalPriceToggle = useSelector(getShowTotalPriceToggle)
  const hasMarginTop = !anchorHotelId && !showTotalPriceToggle

  const {
    getFilters,
    toggleFilters,
    toggleMultiFilter,
    submitFilters,
    clearFiltersAndApply
  } = useFiltersForm(allFilterKeys, 'PartialMatchBanner')

  const filters = getFilters()

  const trackPartialResultsBannerClick = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.PartialResultsBanner,
        action: Action.Clicked
      })
    )
  }, [dispatch])

  const handleClearClick = () => {
    trackPartialResultsBannerClick()
    clearFiltersAndApply()
  }

  const handleFilterChange = useCallback<typeof toggleFilters>(
    updatedFilters => {
      trackPartialResultsBannerClick()
      submitFilters(updatedFilters)
    },
    [trackPartialResultsBannerClick, submitFilters]
  )

  const handleMultiFilterChange = useCallback<typeof toggleMultiFilter>(
    (key, value) => {
      const removeMultiValue = (filterValue: string | string[] | number) => {
        if (
          typeof filterValue === 'string' ||
          typeof filterValue === 'number'
        ) {
          return undefined
        }

        return filterValue.filter(item => item !== value)
      }
      trackPartialResultsBannerClick()
      submitFilters({
        ...filters,
        [key]: removeMultiValue(filters[key])
      })
    },
    [trackPartialResultsBannerClick, submitFilters, filters]
  )

  const {formatMessage} = useIntl()

  return (
    <ContentWrapper
      data-id="PartialMatchBanner"
      className={cx(hasMarginTop && '--margin-top')}
    >
      <AlertWrapper
        dataId="PartialMatchBannerTitle"
        title={formatMessage(titleMessage, {count: numResults})}
        variant="warning"
      >
        <SubtitleWrapper>
          <FormattedMessageWrapper
            id="mapNoResults.tryRemoveFilters"
            defaultMessage="Try adjusting your search criteria or removing some filters."
          />
        </SubtitleWrapper>

        <ScrollWrapper>
          <HorizontalScrollComponent rowGap="s200" columnGap="s200">
            <>
              {PillComponents.map((PillComponent, index) => (
                <PillComponent
                  key={`pill_${index}`}
                  getFilters={getFilters}
                  toggleMultiFilter={handleMultiFilterChange}
                  toggleFilters={handleFilterChange}
                  isActive={false}
                />
              ))}
            </>
            <VerticalDivider />
            <ClearAllWrapper>
              <Button
                variant="ghost"
                size="md"
                iconStart={<Icon name="Trash2" />}
                onClick={handleClearClick}
              >
                <FormattedMessageWrapper
                  id="clearAll"
                  defaultMessage="Clear all"
                />
              </Button>
            </ClearAllWrapper>
          </HorizontalScrollComponent>
        </ScrollWrapper>
      </AlertWrapper>
    </ContentWrapper>
  )
}
